/**
 * @requirement S-043 AC #3 — canonical action catalogue seeded at
 *   app boot. Handlers dispatch to console.info for MVP; real
 *   implementations wire to the Zustand store + router when the
 *   editor route lands its full action layer.
 *
 * S-018 lit up the static `Edit` entries below: `edit.undo` /
 * `edit.redo` dispatch to `useHistoryStore`. Rotate palette entries
 * (`page.rotate-cw` / `page.rotate-ccw`) are dynamically registered
 * by the Editor route (see `apps/web/src/routes/Editor.tsx`) so the
 * closures capture the mounted-hook rotate handler; unregistered on
 * unmount to avoid stale-doc closures.
 */

import { useHistoryStore } from '../../stores/useHistoryStore';

import type { Action } from './types';

export function defaultActions(): Action[] {
  return [
    {
      id: 'file.open',
      label: 'Open document',
      keywords: ['file', 'pick', 'upload'],
      shortcut: '⌘O',
      group: 'File',
      run: () => console.info('[cmd] file.open'),
    },
    {
      id: 'file.save',
      label: 'Save',
      keywords: ['persist', 'download'],
      shortcut: '⌘S',
      group: 'File',
      run: () => console.info('[cmd] file.save'),
    },
    {
      id: 'file.download',
      label: 'Download',
      keywords: ['export'],
      shortcut: '⌘⇧S',
      group: 'File',
      run: () => console.info('[cmd] file.download'),
    },
    {
      id: 'view.zoom-in',
      label: 'Zoom in',
      keywords: ['larger'],
      shortcut: '⌘+',
      group: 'View',
      run: () => console.info('[cmd] view.zoom-in'),
    },
    {
      id: 'view.zoom-out',
      label: 'Zoom out',
      keywords: ['smaller'],
      shortcut: '⌘-',
      group: 'View',
      run: () => console.info('[cmd] view.zoom-out'),
    },
    {
      id: 'view.fit',
      label: 'Fit to window',
      keywords: ['zoom', 'reset'],
      shortcut: '⌘0',
      group: 'View',
      run: () => console.info('[cmd] view.fit'),
    },
    {
      id: 'nav.next-page',
      label: 'Next page',
      keywords: ['forward'],
      shortcut: '⌘→',
      group: 'Navigate',
      run: () => console.info('[cmd] nav.next-page'),
    },
    {
      id: 'nav.prev-page',
      label: 'Previous page',
      keywords: ['back'],
      shortcut: '⌘←',
      group: 'Navigate',
      run: () => console.info('[cmd] nav.prev-page'),
    },
    {
      id: 'edit.undo',
      label: 'Undo',
      keywords: ['revert', 'back'],
      shortcut: '⌘Z',
      group: 'Edit',
      run: () => useHistoryStore.getState().undo(),
    },
    {
      id: 'edit.redo',
      label: 'Redo',
      keywords: ['forward', 'repeat'],
      shortcut: '⌘⇧Z',
      group: 'Edit',
      run: () => useHistoryStore.getState().redo(),
    },
    {
      id: 'account.sign-in',
      label: 'Sign in',
      keywords: ['auth', 'login'],
      group: 'Account',
      run: () => console.info('[cmd] account.sign-in'),
    },
    {
      id: 'theme.toggle',
      label: 'Toggle theme',
      keywords: ['dark', 'light', 'appearance'],
      group: 'Appearance',
      run: () => console.info('[cmd] theme.toggle'),
    },
    {
      id: 'app.help',
      label: 'Help',
      keywords: ['shortcuts', 'docs'],
      shortcut: '?',
      group: 'Help',
      run: () => console.info('[cmd] app.help'),
    },
  ];
}
